import { readApiError } from "_shared/_errors/api-error";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { proposalCollectionItemConflictKeyAspect } from "work/proposals/negotiation/proposal-collection-item-revert";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { readProposalOperationValue } from "work/proposals/negotiation/proposal-operation-projector";

/** The API's code for a save that updates an item the proposal does not hold. Mirrors the controller's. */
export const PROPOSAL_OPERATION_TARGET_MISSING_CODE = "proposal_operation_target_missing";

/** The item a refused save tried to change: the collection, the item, and the field within it. */
export type ProposalMissingItemTarget = {
  path: string;
  itemId: string;
  conflictKey: string;
};

const FEE_CATEGORIES_PATH = "/feeSchedule/categories";

/**
 * The item a save was refused for, when it was refused because the item is not on the proposal (#6612).
 *
 * Accepts both rejection shapes, the bare error a `.unwrap()` caller receives and the `{ error }` that
 * `onQueryStarted` does, as `readProposalCommandErrorDetail` does. Answers undefined for every other
 * failure, so the caller's own message stands.
 */
export function readProposalMissingItemTarget(failure: unknown): ProposalMissingItemTarget | undefined {
  const wrapped = typeof failure === "object" && failure !== null && "error" in failure ? failure.error : undefined;
  return targetOf(failure) ?? (wrapped === undefined ? undefined : targetOf(wrapped));
}

/**
 * The sentence for a save refused because it changed an item the proposal does not hold.
 *
 * The server cannot name the item, because it does not have it. The viewer's own copy usually does:
 * an item that is there is one they added and have not saved, and an item that is not was removed by
 * somebody else. Either way the sentence names the field, and never the operation kind the server
 * refused, which tells the reader nothing about what to do.
 */
export function describeProposalMissingItemSaveFailure(
  target: ProposalMissingItemTarget,
  proposal: ProposalJSON | undefined,
): string {
  if (target.path !== FEE_CATEGORIES_PATH) {
    return "Couldn't save a change to an item that is no longer on the proposal.";
  }

  const aspect = proposalCollectionItemConflictKeyAspect(target.path, target.itemId, target.conflictKey);
  const name = categoryName(proposal, target.itemId);
  if (!aspect) {
    return name
      ? `Couldn't save the fee category "${name}": it isn't saved yet.`
      : "Couldn't save a fee category that is no longer on the proposal.";
  }
  const field = feeCategoryFieldName(aspect);
  return name
    ? `Couldn't save the ${field} for "${name}": that category isn't saved yet.`
    : `Couldn't save the ${field} for a fee category that is no longer on the proposal.`;
}

function targetOf(error: unknown): ProposalMissingItemTarget | undefined {
  const apiError = readApiError(error);
  if (apiError.code !== PROPOSAL_OPERATION_TARGET_MISSING_CODE) return undefined;
  const [path] = apiError.fieldErrors.path ?? [];
  const [itemId] = apiError.fieldErrors.itemId ?? [];
  const [conflictKey] = apiError.fieldErrors.conflictKey ?? [];
  if (!path || !itemId || !conflictKey) return undefined;
  return { path, itemId, conflictKey };
}

function categoryName(proposal: ProposalJSON | undefined, itemId: string): string | undefined {
  if (!proposal) return undefined;
  const categories = readProposalOperationValue(proposal, FEE_CATEGORIES_PATH);
  if (!Array.isArray(categories)) return undefined;
  const category: unknown = categories.find((item) => proposalCollectionItemHasIdentity(item, itemId));
  if (typeof category !== "object" || category === null || !("name" in category)) return undefined;
  return typeof category.name === "string" && category.name.trim() ? category.name : undefined;
}

/** The words the server's resolution messages use for the same columns. */
function feeCategoryFieldName(aspect: string): string {
  switch (aspect) {
    case "feeRate":
    case "feeCurrency":
    case "feeUnit":
      return "fee";
    case "type":
      return "fee or expense type";
    case "teamMember":
      return "team member";
    default:
      return aspect;
  }
}
